// web/src/components/ceremony/VaultSafe.tsx — the animated vault safe (M3.1).
//
// PURE, PRESENTATIONAL SVG. One input: { submitted, threshold, status }. It owns
// no ceremony logic — the container (UnsealCeremony) computes state from the
// authoritative shard tally and hands it down. Three visual states map 1:1 to the
// request lifecycle (motion-spec §2):
//
//   sealed     — door shut, all K bolts thrown, dial at 0deg, no motion.
//   collecting — >=1 and <K shards in; bolts retract one-per-shard, dial steps,
//                a slow throb on the ring signals the session is live.
//   unlocked   — K reached; the final bolt clears, the dial completes travel, the
//                door swings open ONCE (the one earned dramatic move).
//
// Token-only: every duration/easing/colour is a CSS var (see styles/ceremony.css,
// which references design/tokens.json#motion via the --dur-ceremony-* names from
// motion-spec §1). No JS animation — CSS transitions on token vars, so the
// reduced-motion media query is the single, honest off-switch (motion-spec §5).
//
// Reduced-motion parity: a stepped progress meter ("◼◼◻ 2 of 3 shards submitted")
// is ALWAYS rendered (visually hidden while the safe animates, shown when motion
// is reduced) so screen-reader and reduced-motion users get identical information
// (motion-spec §4). Bolts/dial render at their final positions with no transition.
//
// SEALING VARIANT (the locking ceremony — the MIRROR of the unseal opening).
// `mode="seal"` runs the choreography in reverse for the initiator's seal-complete
// moment: the door swings SHUT, the K bolts drive home one by one, and the dial
// spins to the locked position. Two seal states map to that arc:
//
//   sealing  — door is swinging closed; bolts driving home progressively.
//   locked   — fully sealed: door shut, all bolts thrown, dial at the locked stop.
//
// It honors the same token-only contract (motion-spec §1) and reduced-motion
// off-switch as the unseal direction: under reduced motion the door/bolts/dial show
// their final (locked) positions instantly and the stepped meter reads "Sealed".
import React from 'react';

export type SafeState = 'sealed' | 'collecting' | 'unlocked' | 'sealing' | 'locked';

/** Direction the safe choreographs. `unseal` opens at quorum; `seal` closes on seal. */
export type SafeMode = 'unseal' | 'seal';

export interface VaultSafeProps {
  /** Shards submitted so far (the retracted-bolt count). 0 <= submitted <= threshold. */
  submitted: number;
  /** K — the quorum threshold; equals the number of radial bolts drawn. */
  threshold: number;
  /** Resolved lifecycle state; defaults to a derive from submitted vs threshold. */
  state?: SafeState;
  /**
   * Choreography direction. `unseal` (default) opens the safe at quorum; `seal`
   * runs the mirror — the door drives shut and the bolts throw home (the locking
   * ceremony). In `seal` mode the visible meter reads "Sealing/Sealed", not shards.
   */
  mode?: SafeMode;
  /** Optional reveal content rendered behind the door once unlocked (the interior). */
  children?: React.ReactNode;
}

// SVG geometry constants (internal to the safe; not tokens — they are layout, not
// motion/colour). The dial completes a 90deg clockwise arc across 0->K (spec §3.2).
const VIEW = 200;
const CENTER = VIEW / 2;
const BOLT_RING_RADIUS = 64; // distance of an extended bolt's centre from the hub
const BOLT_RETRACT = 22; // how far inward a cleared bolt slides toward the hub
const DIAL_TOTAL_ARC = 90; // total dial travel in degrees (motion-spec §3.2)

/** Derive the safe state from the tally when not explicitly provided. */
function deriveState(submitted: number, threshold: number): SafeState {
  if (submitted <= 0) return 'sealed';
  if (submitted >= threshold) return 'unlocked';
  return 'collecting';
}

/** Position of the n-th bolt (0-indexed) evenly around the hub, starting at top. */
function boltAngle(index: number, count: number): number {
  return (360 / count) * index - 90; // -90 so bolt 0 sits at 12 o'clock
}

export function VaultSafe({
  submitted,
  threshold,
  state,
  mode = 'unseal',
  children,
}: VaultSafeProps): React.JSX.Element {
  const k = Math.max(1, threshold);
  const n = Math.min(Math.max(0, submitted), k);
  const sealing = mode === 'seal';
  const resolved = state ?? deriveState(n, k);

  // Door position. UNSEAL: shut until quorum, then swings OPEN at `unlocked`. SEAL
  // (the mirror): the door stays OPEN while the bolts drive home (`sealing`), then
  // swings SHUT as the final, dramatic move when fully `locked`.
  const isOpen = sealing ? resolved !== 'locked' : resolved === 'unlocked';

  // Dial rotates n/K of the total arc. Unseal: 0 → full at quorum (opening). Seal:
  // the dial spins TOWARD the locked stop as bolts throw home (n/K of the arc, but
  // the door is closing, so the same n maps to "more locked").
  const dialDeg = (n / k) * DIAL_TOTAL_ARC;

  const bolts = Array.from({ length: k }, (_, i) => {
    const angle = (boltAngle(i, k) * Math.PI) / 180;
    // Extended position on the bolt ring.
    const x = CENTER + Math.cos(angle) * BOLT_RING_RADIUS;
    const y = CENTER + Math.sin(angle) * BOLT_RING_RADIUS;
    // Retract vector points back toward the hub (centre).
    const retractX = -Math.cos(angle) * BOLT_RETRACT;
    const retractY = -Math.sin(angle) * BOLT_RETRACT;
    // Unseal: a CLEARED (retracted) bolt is one whose shard arrived (i < n).
    // Seal: bolts start retracted (open) and DRIVE HOME (extend) one-per-step — so
    // a bolt is "thrown/home" once i < n; the first n are no longer cleared.
    const cleared = sealing ? i >= n : i < n;
    return { i, x, y, retractX, retractY, cleared };
  });

  const segs = Array.from({ length: k }, (_, i) => i < n);
  const meterText = sealing
    ? resolved === 'locked'
      ? `${n} of ${k} bolts thrown — sealed`
      : `${n} of ${k} bolts thrown — sealing`
    : resolved === 'unlocked'
      ? `${n} of ${k} shards — unlocked`
      : `${n} of ${k} shards submitted`;

  // Drive the door open/shut purely off a resolved class so the CSS transition (and
  // its reduced-motion override) is the single source of timing (motion-spec §5).
  const stateClass = `bgSafe${resolved.charAt(0).toUpperCase()}${resolved.slice(1)}`;
  const openClass = isOpen ? 'bgSafeOpen' : 'bgSafeShut';

  // Dial rim tick marks — graduated graduations around the combination dial. A
  // longer "major" tick every fifth mark, the rest minor. Pure decoration of the
  // dial face (layout, not motion/colour), so computed here as SVG geometry.
  const DIAL_R = 27; // dial face radius
  const TICKS = 40;
  const ticks = Array.from({ length: TICKS }, (_, i) => {
    const a = ((360 / TICKS) * i - 90) * (Math.PI / 180);
    const major = i % 5 === 0;
    const outer = DIAL_R - 1.5;
    const inner = outer - (major ? 5 : 3);
    return {
      i,
      x1: CENTER + Math.cos(a) * outer,
      y1: CENTER + Math.sin(a) * outer,
      x2: CENTER + Math.cos(a) * inner,
      y2: CENTER + Math.sin(a) * inner,
      major,
    };
  });

  // Knurled grip — short radial serrations around the dial's grip ring, the
  // "milled" texture that reads as a turnable knob.
  const KNURLS = 36;
  const KNURL_R = 21;
  const knurls = Array.from({ length: KNURLS }, (_, i) => {
    const a = ((360 / KNURLS) * i) * (Math.PI / 180);
    return {
      i,
      x1: CENTER + Math.cos(a) * (KNURL_R - 2.5),
      y1: CENTER + Math.sin(a) * (KNURL_R - 2.5),
      x2: CENTER + Math.cos(a) * KNURL_R,
      y2: CENTER + Math.sin(a) * KNURL_R,
    };
  });

  // Frame corner bolts/rivets — one inset at each corner of the safe body.
  const RIVET = 23;
  const rivets = [
    [RIVET, RIVET],
    [VIEW - RIVET, RIVET],
    [RIVET, VIEW - RIVET],
    [VIEW - RIVET, VIEW - RIVET],
  ];

  return (
    <div className={`bgSafe ${stateClass} ${openClass}`}>
      <svg
        className="bgSafeSvg"
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        role="img"
        aria-label={`Vault safe, ${meterText}`}
        style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block' }}
      >
        {/* Metallic / lighting gradient defs — referenced by class via fill="url()".
            These are presentation (brushed-steel feel); the colour STOPS are token
            vars (see ceremony.css), so the fills stay token-driven. */}
        <defs>
          <linearGradient id="bgSteelBody" x1="0" y1="0" x2="0" y2="1">
            <stop className="bgStopSteelHi" offset="0" />
            <stop className="bgStopSteelMid" offset="0.5" />
            <stop className="bgStopSteelLo" offset="1" />
          </linearGradient>
          <linearGradient id="bgSteelDoor" x1="0.1" y1="0" x2="0.9" y2="1">
            <stop className="bgStopDoorHi" offset="0" />
            <stop className="bgStopDoorMid" offset="0.55" />
            <stop className="bgStopDoorLo" offset="1" />
          </linearGradient>
          <radialGradient id="bgDialMetal" cx="0.4" cy="0.35" r="0.75">
            <stop className="bgStopDialHi" offset="0" />
            <stop className="bgStopDialMid" offset="0.6" />
            <stop className="bgStopDialLo" offset="1" />
          </radialGradient>
          <radialGradient id="bgCavity" cx="0.5" cy="0.5" r="0.75">
            <stop className="bgStopCavityLo" offset="0" />
            <stop className="bgStopCavityHi" offset="1" />
          </radialGradient>
          <radialGradient id="bgGlow" cx="0.5" cy="0.5" r="0.6">
            <stop className="bgStopGlowHot" offset="0" />
            <stop className="bgStopGlowWarm" offset="0.5" />
            <stop className="bgStopGlowEdge" offset="1" />
          </radialGradient>
          <linearGradient id="bgBoltMetal" x1="0" y1="0" x2="1" y2="1">
            <stop className="bgStopBoltHi" offset="0" />
            <stop className="bgStopBoltLo" offset="1" />
          </linearGradient>
          <linearGradient id="bgRivetMetal" x1="0" y1="0" x2="0" y2="1">
            <stop className="bgStopRivetHi" offset="0" />
            <stop className="bgStopRivetLo" offset="1" />
          </linearGradient>
          {/* Soft drop shadow for chunky parts (bolts, door edge). */}
          <filter id="bgPartShadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="2.5"
              floodColor="#000"
              floodOpacity="0.55"
            />
          </filter>
        </defs>

        {/* Outer drop shadow plate — gives the whole safe weight on the stage. */}
        <rect
          className="bgSafeShadow"
          x={10}
          y={14}
          width={VIEW - 20}
          height={VIEW - 20}
          rx={18}
        />

        {/* Safe body — brushed steel frame with a metallic gradient fill. */}
        <rect
          className="bgSafeBody"
          x={12}
          y={12}
          width={VIEW - 24}
          height={VIEW - 24}
          rx={18}
        />
        {/* Frame inner bevel — a recessed lip the door sits inside. */}
        <rect
          className="bgFrameBevel"
          x={20}
          y={20}
          width={VIEW - 40}
          height={VIEW - 40}
          rx={13}
        />

        {/* Hinges on the door's left edge — visible knuckles, two of them. */}
        <g className="bgHinges" aria-hidden="true">
          <rect className="bgHinge" x={16} y={58} width={10} height={26} rx={3} />
          <rect className="bgHinge" x={16} y={116} width={10} height={26} rx={3} />
        </g>

        {/* Frame corner bolts/rivets. */}
        <g className="bgRivets" aria-hidden="true">
          {rivets.map(([cx, cy], i) => (
            <g key={i}>
              <circle className="bgRivet" cx={cx} cy={cy} r={4.5} />
              <circle className="bgRivetHi" cx={cx - 1} cy={cy - 1} r={1.4} />
            </g>
          ))}
        </g>

        {/* The cavity (recessed interior) revealed once the door swings open.
            A dark recess with a warm interior glow when unlocked. */}
        <g className="bgCavity">
          <rect
            className="bgCavityWall"
            x={26}
            y={26}
            width={VIEW - 52}
            height={VIEW - 52}
            rx={11}
          />
          <rect
            className="bgInterior"
            x={26}
            y={26}
            width={VIEW - 52}
            height={VIEW - 52}
            rx={11}
          />
          <ellipse
            className="bgInteriorGlow"
            cx={CENTER}
            cy={CENTER}
            rx={56}
            ry={56}
          />
        </g>

        {/* Radial bolts — one per required shard (K). Chunky, shadowed; thrown
            (seated into the frame) by default, retract into the door when cleared. */}
        {bolts.map((b) => (
          <g
            key={b.i}
            className={`bgBolt ${b.cleared ? 'bgBoltCleared' : ''}`}
            style={
              {
                '--bolt-retract-x': `${b.retractX}px`,
                '--bolt-retract-y': `${b.retractY}px`,
              } as React.CSSProperties
            }
          >
            <rect
              className="bgBoltBody"
              x={b.x - 7}
              y={b.y - 7}
              width={14}
              height={14}
              rx={3.5}
            />
            <rect
              className="bgBoltCap"
              x={b.x - 7}
              y={b.y - 7}
              width={14}
              height={4}
              rx={2}
            />
          </g>
        ))}

        {/* Door panel — hinged on its left edge; swings open once at K. Carries a
            visible thickness/edge when shut so the recess reads as real depth. */}
        <g className="bgDoor">
          {/* Door edge / thickness — the side of the slab, visible as it swings. */}
          <rect
            className="bgDoorEdge"
            x={24}
            y={24}
            width={VIEW - 48}
            height={VIEW - 48}
            rx={12}
          />
          {/* Door face — brushed steel slab. */}
          <rect
            className="bgDoorFace"
            x={26}
            y={26}
            width={VIEW - 52}
            height={VIEW - 52}
            rx={11}
          />
          {/* Door inner bevel — a recessed panel on the slab face. */}
          <rect
            className="bgDoorBevel"
            x={38}
            y={38}
            width={VIEW - 76}
            height={VIEW - 76}
            rx={8}
          />

          {/* Combination dial — graduated rim, knurled grip, rotating pointer. */}
          <g className="bgDialWrap">
            {/* Stationary mounting boss the dial seats into. */}
            <circle className="bgDialBoss" cx={CENTER} cy={CENTER} r={DIAL_R + 4} />
            {/* Fixed index mark at 12 o'clock (the reference the pointer reads against). */}
            <line
              className="bgDialIndex"
              x1={CENTER}
              y1={CENTER - DIAL_R - 5}
              x2={CENTER}
              y2={CENTER - DIAL_R + 1}
            />
            {/* The rotating dial: face + ticks + knurl + pointer all turn together. */}
            <g
              className="bgDial"
              style={{ '--dial-deg': `${dialDeg}deg` } as React.CSSProperties}
            >
              <circle className="bgDialFace" cx={CENTER} cy={CENTER} r={DIAL_R} />
              {/* Graduated tick marks around the rim. */}
              <g className="bgDialTicks" aria-hidden="true">
                {ticks.map((t) => (
                  <line
                    key={t.i}
                    className={`bgDialTick ${t.major ? 'bgDialTickMajor' : ''}`}
                    x1={t.x1}
                    y1={t.y1}
                    x2={t.x2}
                    y2={t.y2}
                  />
                ))}
              </g>
              {/* Knurled grip ring. */}
              <g className="bgDialKnurls" aria-hidden="true">
                {knurls.map((kk) => (
                  <line
                    key={kk.i}
                    className="bgDialKnurl"
                    x1={kk.x1}
                    y1={kk.y1}
                    x2={kk.x2}
                    y2={kk.y2}
                  />
                ))}
              </g>
              {/* Knurled grip hub. */}
              <circle className="bgDialHub" cx={CENTER} cy={CENTER} r={9} />
              {/* Indicator pointer — reads against the fixed index. */}
              <polygon
                className="bgDialPointer"
                points={`${CENTER},${CENTER - DIAL_R + 2} ${CENTER - 3},${CENTER - 9} ${CENTER + 3},${CENTER - 9}`}
              />
            </g>
          </g>
        </g>
      </svg>

      {/* Reveal content host (the safe's contents emerge behind the open door) */}
      {resolved === 'unlocked' && children}

      {/* Stepped progress meter — ALWAYS rendered for parity (motion-spec §4).
          Visually hidden while the safe animates; shown under reduced motion. */}
      <div className="bgSafeMeter bgSafeMeterVisual">
        <div className="bgSafeMeterSegs" aria-hidden="true">
          {segs.map((on, i) => (
            <span
              key={i}
              className={`bgSafeMeterSeg ${on ? 'bgSafeMeterSegOn' : ''}`}
            />
          ))}
        </div>
        <span className="bgSafeMeterLabel">{meterText}</span>
      </div>
    </div>
  );
}

export default VaultSafe;
